import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Pagination } from "swiper";
import { Account } from "../../types";
import Typography from "../typography/Typography";
import { Button } from "../button";
import Stack from "../stack/Stack";
import { useBackgroundColor, useResponsiveType } from "../../utils";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import Details from "../details/Details";
import AccountFees from "../accountFees/AccountFees";
import Divider from "../divider/Divider";
import "swiper/swiper-bundle.min.css";
import "swiper/css/pagination";
import { ThemeButtonKeys } from "../../styles/tokens/buttons";
import Link from "../link/Link";

// Interface

export interface AccountsProps {
  accounts: Account[];
  displayCostInColourBlock?: boolean;
  pageUrl: string;
}

export interface StyledHeadingProps {
  backgroundColor: ThemeColorsKeys;
}

export interface StyledAccountsProps {
  numberOfAccounts: number;
}

export interface StyledTabsProps {
  numberOfAccounts: number;
}

// Styles

const StyledAccounts = styled.div<StyledAccountsProps>`
  ${({ theme: { colors, borderRadii }, numberOfAccounts }) => css`
    display: grid;
    grid-template-columns: repeat(${numberOfAccounts}, 1fr);

    > * {
      border-left: 1px solid ${colors.darkBlue};
      overflow: hidden;
      border-top: 1px solid ${colors.darkBlue};
      border-bottom: 1px solid ${colors.darkBlue};

      &:last-of-type {
        border-right: 1px solid ${colors.darkBlue};
        border-top-right-radius: ${borderRadii.small};
        border-bottom-right-radius: ${borderRadii.small};
      }

      &:first-of-type {
        border-top-left-radius: ${borderRadii.small};
        border-bottom-left-radius: ${borderRadii.small};
      }
    }
  `};
`;

const StyledAccount = styled.div`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
  `};
`;

const StyledHeading = styled(Stack)<StyledHeadingProps>`
  ${({ theme: { space, colors, media, borderRadii }, backgroundColor }) => css`
    ${useBackgroundColor(backgroundColor)}
    padding: ${space.fluidMediumToLarge};
    border-bottom: 1px solid ${colors.darkBlue};
  `};
`;

const StyledBody = styled(Stack)`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    padding: ${space.fluidMediumToLarge};
    ${useBackgroundColor("white")}
    flex-grow: 1;

    a {
      width: 100%;
    }
  `};
`;

const StyledHeader = styled(Stack)`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  `};
`;

const StyledSubheading = styled(Typography)`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    flex-grow: 1;
  `};
`;

const StyledMobileAccounts = styled.div`
  ${({ theme: { maxMedia, borders, borderRadii } }) => css`
    display: none;

    // Mobile only styles

    @media (max-width: ${maxMedia.large}) {
      display: block;

      // Between mobile and desktop sizes

      @media (max-width: ${maxMedia.large}) {
        max-width: 579px;
      }

      .swiper {
        overflow: visible;
      }

      .swiper-slide {
        height: auto;
        max-width: 100%;
      }

      .swiper-button-next,
      .swiper-button-prev,
      .swiper-scrollbar {
        display: none;
      }

      .swiper-slide {
        height: auto;
        overflow: hidden;
        border-left: ${borders.darkBlue};
        border-top: ${borders.darkBlue};
        border-bottom: ${borders.darkBlue};

        &:last-of-type {
          border-top-right-radius: ${borderRadii.small};
          border-bottom-right-radius: ${borderRadii.small};
          border-right: ${borders.darkBlue};
        }

        &:first-of-type {
          border-top-left-radius: ${borderRadii.small};
          border-bottom-left-radius: ${borderRadii.small};
        }
      }
    }
  `};
`;

const StyledDesktopAccounts = styled.div`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.large}) {
      display: none;
    }
  `};
`;

const StyledTabs = styled.div<StyledTabsProps>`
  ${({
    theme: { colors, space, borderRadii, maxMedia },
    numberOfAccounts,
  }) => css`
    display: flex;
    margin-bottom: ${space.medium};
    display: grid;
    grid-template-columns: repeat(${numberOfAccounts}, 1fr);
    border-radius: ${borderRadii.small};
    border: 1px solid currentColor;
    overflow: hidden;
    ${useBackgroundColor("white")}

    button {
      ${useResponsiveType("bodyBold")}
      ${useBackgroundColor("white")}
      padding: ${space.xxxxxSmall};

      &:first-of-type {
        border-right: 1px solid currentColor;
      }

      &:last-of-type {
        border-left: 1px solid currentColor;
      }

      &.is-active {
        background-color: ${colors.zemplerBlue};
      }
    }
  `};
`;

const StyledDetails = styled(Stack)`
  ${({ theme: { space } }) => css``};
`;

const StyledAction = styled.div`
  ${({ theme: { space } }) => css`
    padding: ${space.fluidMediumToLarge};
    padding-top: 0;
    ${useBackgroundColor("white")}
    display: flex;
    flex-direction: column;
    gap: ${space.fluidXSmallToSmall};
    justify-content: center;

    a {
      width: 100%;
      text-align: center;
      justify-content: center;
    }
  `};
`;

// JSX

const useDebouncedEffect = (effect: () => void, delay: number, deps: any[]) => {
  const callback = useRef<() => void>();
  useEffect(() => {
    callback.current = effect;
  }, [effect]);

  useEffect(() => {
    const handler = () => {
      if (callback.current) callback.current();
    };

    const debouncedHandler = debounce(handler, delay);

    window.addEventListener("resize", debouncedHandler);

    return () => {
      window.removeEventListener("resize", debouncedHandler);
    };
  }, deps);
};

const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const AccountCard: FC<
  Account & {
    subheadingRef: React.RefObject<HTMLDivElement>;
    benefitsTableRef: React.RefObject<HTMLDivElement>;
  }
> = ({
  name,
  subHeading,
  monthlyCost,
  details,
  benefits,
  primaryButton,
  secondaryLink,
  allFeesLink,
  colour = "zemplerBlue",
  displayCostInColourBlock,
  accountFeeHeading,
  accountFeeSubHeading,
  fees,
  buttonColour,
  limits,
  feeSmallPrint,
  subheadingRef,
  benefitsTableRef,
}) => {
  function makeFirstCharLowercase(str: string): string {
    if (str.length === 0) return str; // Return empty string if input is empty

    // Convert first character to lowercase and concatenate it with the rest of the string
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  return (
    <StyledAccount>
      <StyledHeading backgroundColor={colour} gap="fluidXxSmallToxSmall">
        <Typography component="h3" variant="headingSmall">
          {name}
        </Typography>
        {displayCostInColourBlock && (
          <>
            <Typography
              component="p"
              variant="secondaryHeadingSmallToMediumBold"
            >
              {monthlyCost}
            </Typography>
            <div ref={subheadingRef}>
              <StyledSubheading
                component="p"
                variant="secondaryHeadingSmallBold"
              >
                {subHeading}
              </StyledSubheading>
            </div>
          </>
        )}
      </StyledHeading>
      <StyledBody gap="fluidSmallToXLarge">
        {!displayCostInColourBlock && (
          <StyledHeader gap="fluidXxSmallToxSmall">
            <Typography
              component="p"
              variant="secondaryHeadingSmallToMediumBold"
            >
              {monthlyCost}
            </Typography>
            <div ref={subheadingRef}>
              <StyledSubheading
                component="p"
                variant="secondaryHeadingSmallBold"
              >
                {subHeading}
              </StyledSubheading>
            </div>
          </StyledHeader>
        )}

        {benefits && (
          <div ref={benefitsTableRef}>
            <Details items={benefits} />{" "}
          </div>
        )}

        {(fees && fees.length > 0) || details.length > 0 ? (
          <StyledDetails gap="fluidXSmallToSmall">
            <Details items={details} />
            {fees && fees.length > 0 && (
              <>
                <Divider />
                <AccountFees
                  heading={accountFeeHeading}
                  subHeading={accountFeeSubHeading}
                  fees={fees}
                  limits={limits}
                  accountName={name}
                  feeSmallPrint={feeSmallPrint}
                  allFeesLink={allFeesLink}
                />
              </>
            )}
          </StyledDetails>
        ) : (
          ""
        )}
      </StyledBody>
      {primaryButton || secondaryLink ? (
        <StyledAction>
          {primaryButton && (
            <Button
              variant={
                (makeFirstCharLowercase(
                  buttonColour || ""
                ) as ThemeButtonKeys) || "neutral"
              }
              arrowRight
              {...primaryButton}
              dataLayerClickContext="Accounts - primary button"
              aria-label={`Open ${{ name }}`}
            />
          )}

          {secondaryLink && (
            <Link
              {...secondaryLink}
              dataLayerClickContext="Accounts - secondary link"
              aria-label={`Open ${{ name }}`}
            />
          )}
        </StyledAction>
      ) : (
        ""
      )}
    </StyledAccount>
  );
};

export const Accounts: FC<AccountsProps> = ({
  accounts,
  displayCostInColourBlock,
  pageUrl,
}) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const swiperRef = useRef<Swiper | null>(null); // Ref to store the Swiper instance
  const subheadingRefs = useRef<(HTMLDivElement | null)[]>([]);
  const benefitsTableRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const onSlideChange = () => {
      if (swiperRef.current !== null && swiperRef.current.swiper !== null) {
        setActiveSlideIndex(swiperRef.current.activeIndex);
      }
    };

    if (swiperRef.current !== null && swiperRef.current.swiper !== null) {
      swiperRef.current.on("slideChange", onSlideChange);
    }

    return () => {
      if (swiperRef.current !== null && swiperRef.current.swiper !== null) {
        swiperRef.current.off("slideChange", onSlideChange);
      }
    };
  }, []);

  const handleButtonClick = (index: number) => {
    if (swiperRef.current !== null && swiperRef.current.swiper !== null) {
      swiperRef.current.slideTo(index); // Slide to the corresponding index
    }
  };

  // Function to set equal heights for subheadings
  const setEqualHeights = useCallback(() => {
    let maxHeight = 0;

    // Subheadings

    subheadingRefs.current.forEach((subheading) => {
      if (subheading) {
        subheading.style.height = "auto"; // Reset height
        const height = subheading.offsetHeight;
        if (height > maxHeight) maxHeight = height;
      }
    });
    subheadingRefs.current.forEach((subheading) => {
      if (subheading) {
        subheading.style.height = `${maxHeight}px`;
      }
    });

    // Fees tables

    benefitsTableRefs.current.forEach((feesTable) => {
      if (feesTable) {
        feesTable.style.height = "auto"; // Reset height
        const height = feesTable.offsetHeight;
        if (height > maxHeight) maxHeight = height;
      }
    });
    benefitsTableRefs.current.forEach((feesTable) => {
      if (feesTable) {
        feesTable.style.height = `${maxHeight}px`;
      }
    });
  }, []);

  // Apply equal heights on mount and on window resize
  useDebouncedEffect(setEqualHeights, 100, [accounts, activeSlideIndex]);

  useEffect(() => {
    setEqualHeights(); // Initial call on mount
  }, [setEqualHeights]);

  return (
    <div>
      <StyledMobileAccounts>
        <StyledTabs numberOfAccounts={accounts.length}>
          {accounts?.map((account, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleButtonClick(index)}
              className={index === activeSlideIndex ? "is-active" : ""}
            >
              {account.shortName || account.name.replace("Business ", "")}
            </button>
          ))}
        </StyledTabs>

        <Swiper
          modules={[Pagination, A11y]}
          spaceBetween={0}
          slidesPerView={1}
          onSwiper={(swiper) => (swiperRef.current = swiper)} // Assigning swiper instance to ref
        >
          {accounts?.map((account, index) => (
            <SwiperSlide key={index}>
              <AccountCard
                key={index}
                {...account}
                displayCostInColourBlock={displayCostInColourBlock}
                subheadingRef={(el) => (subheadingRefs.current[index] = el)}
                benefitsTableRef={(el) => (benefitsTableRefs.current[index] = el)}
                pageUrl={pageUrl}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </StyledMobileAccounts>
      <StyledDesktopAccounts>
        <StyledAccounts numberOfAccounts={accounts.length}>
          {accounts?.map((account, index) => (
            <AccountCard
              key={index}
              {...account}
              displayCostInColourBlock={displayCostInColourBlock}
              subheadingRef={(el) => (subheadingRefs.current[index] = el)}
              benefitsTableRef={(el) => (benefitsTableRefs.current[index] = el)}
              pageUrl={pageUrl}
            />
          ))}
        </StyledAccounts>
      </StyledDesktopAccounts>
    </div>
  );
};
export default Accounts;
